<template>
  <div class="field has-addons">
    <p class="control is-expanded">
      <span
        class="input"
        :class="classes"
        readonly
        :disabled="!fieldDefinition.editable || undefined"
        v-on:click="openModal"
        >{{ versionDisplayString }}</span
      >
    </p>
    <p
      class="control"
      v-if="fieldDefinition.editable"
    >
      <button
        title="Select a worker version"
        class="button has-text-primary"
        type="button"
        v-on:click="openModal"
        :disabled="fieldData.loading || undefined"
      >
        <i class="icon-edit"></i>
      </button>
    </p>
    <p
      class="control"
      v-if="fieldDefinition.editable && fieldData.value !== null && !fieldDefinition.required"
    >
      <button
        class="button has-text-danger"
        type="button"
        v-on:click="selectWorkerVersion(null)"
        title="Unselect worker version"
        :disabled="fieldData.loading || undefined"
      >
        <i class="icon-minus"></i>
      </button>
    </p>
  </div>
  <p
    class="help is-danger"
    v-for="(error, i) in [...localErrors, ...fieldData.errors]"
    :key="i"
  >
    {{ error }}
  </p>
  <Modal
    v-if="selectionModal"
    v-model="selectionModal"
    title="Select a worker version"
    is-large
  >
    <Workers
      selectable
      v-on:selected-version="selectWorkerVersion"
    />
    <template v-slot:footer>
      <div class="buttons">
        <button
          type="button"
          class="button"
          title="Select an explicit lack of worker version. This usually means data that has been created manually."
          :disabled="fieldData.loading || undefined"
          v-on:click="selectWorkerVersion(false)"
        >
          Select no worker version
        </button>
        <button
          v-if="!fieldDefinition.required"
          type="button"
          class="button"
          title="Select no value for this field."
          :disabled="fieldData.loading || undefined"
          v-on:click="selectWorkerVersion(null)"
        >
          Unselect
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { isAxiosError } from 'axios'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import Modal from '@/components/Modal.vue'
import { UUID_REGEX } from '@/config'
import { errorParser } from '@/helpers'
import { useWorkerStore } from '@/stores'
import type { WorkerVersion } from '@/types/worker'
import Workers from '@/views/Process/Workers/List.vue'

import { type FieldModel, type FieldProps, ValidationError, defineValidator } from '.'

type Constraint = { type: 'worker_version'; many: false }
const { fieldDefinition } = defineProps<FieldProps<Constraint>>()
const fieldData = defineModel<FieldModel<Constraint>>({ required: true })

const classes = computed(() => ({
  'is-danger': !fieldData.value.valid,
  'is-loading': fieldData.value.loading,
  'is-clickable': fieldDefinition.editable || !!fieldData.value.value,
}))

const selectionModal = ref(false)

const router = useRouter()
const workerStore = useWorkerStore()

const versionDisplayString = computed<string>(() => {
  if (fieldData.value.value === null) return ''
  if (fieldData.value.value === false) return 'No worker version'
  const version = workerStore.workerVersions[fieldData.value.value]
  if (!version) return fieldData.value.value
  return `${version.worker.name} ${version.revision_url ?? `version ${version.version}`} (${version.id})`
})

async function validator(id: unknown) {
  if (id === null || id === false) return
  if (typeof id !== 'string' || !UUID_REGEX.test(id))
    throw new ValidationError([`${id} is not a valid worker version ID`])

  // We already know about this worker version, it is valid
  if (id in workerStore.workerVersions) return

  try {
    await workerStore.getWorkerVersion(id)
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404)
      throw new ValidationError([
        'This worker version does not exist or you do not have access to it',
      ])
    throw new ValidationError([`Could not retrieve this worker version: ${errorParser(err)}`])
  }
}
const { localErrors } = defineValidator(fieldDefinition, fieldData.value, validator)

function openModal() {
  if (fieldData.value.loading) return
  // On an editable field, open the picker modal
  if (fieldDefinition.editable) selectionModal.value = true
  // On a non-editable field, open the model details in a new tab when a model is set
  else if (!fieldData.value.value) return
  else
    window.open(
      router.resolve({ name: 'worker-version', params: { versionId: fieldData.value.value } }).href,
      '_blank',
    )
}

function selectWorkerVersion(workerVersion: WorkerVersion | false | null) {
  if (fieldData.value.loading) return
  fieldData.value.value = workerVersion ? workerVersion.id : workerVersion
  // Close the modal immediately after selecting a version
  selectionModal.value = false
}
</script>

<style lang="css" scoped>
.field + .help {
  margin-top: calc(-1 * var(--bulma-block-spacing));
}
</style>
